/**
 * OnboardingPage — F3 (rebuild visual fiel ao prototipo onboarding.html)
 *
 * Fluxo visivel (3 passos — fiel ao prototipo):
 *   1. account  — nome + e-mail + senha + criacao de tenant
 *   2. sources  — conexao de fontes (Instagram, Meta Ads, Google Drive)
 *   3. success  — confirmacao + timeline de proximos passos + ir ao painel
 *
 * Fluxo interno preservado do F2 (step segment foi colapsado em account):
 *   - Criacao real de tenant via POST /tenants (mock local D-FE-002 enquanto nao existe)
 *   - emitKpi TenantCreated apos criar tenant
 *   - OAuth real via POST /sources/connect/:type -> abre nova aba
 *   - emitKpi FeatureUsed (dashboard-opened) ao avancar para sucesso
 *   - Redirect final para /mirror
 *
 * GAP documentado:
 *   - G-ONB-001: step "segment" removido da UI visivel (nao estava no prototipo);
 *     o segmento padrao 'outro' e enviado junto com a criacao do tenant.
 *     Reintroduzir como step entre account e sources se Product-Designer especificar.
 *   - G-ONB-002: campo "nome do negocio" foi mesclado ao passo 1 (campo adicional);
 *     o prototipo mostra so nome pessoal+email+senha. Incluimos tenantName como
 *     campo separado abaixo da senha para nao perder a criacao de tenant.
 *     Content-Designer deve revisar a copy do label.
 *   - G-ONB-003: autenticacao real (POST /auth/signup) nao esta integrada —
 *     o passo 1 cria o tenant mas nao cria a conta de usuario. Quando
 *     Backend-Engineer entregar POST /auth/signup, integrar aqui e emitir
 *     SignupCompleted server-side.
 *   - D-034 PROVISORIO: cores de fundo dos icones de fonte sao provisorias
 *     ate o owner aprovar os brand guidelines definitivos (ver OnbStep.tsx).
 *
 * Isolamento:
 *   - CSS: importa screen-onboarding.css (nunca global.css)
 *   - Componentes: usa apenas OnbStep.tsx (prefixo Onb*)
 *   - i18n: zero string hardcoded — tudo via t()
 *   - Tokens: zero valor de cor hardcoded — tudo via var(--*)
 */

import '../styles/screen-onboarding.css';

import { useState, useCallback, useRef, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, ptBR } from '../lib/i18n';
import { useAuth } from '../lib/auth-context';
import { apiClient } from '../lib/api-client';
import { emitKpi, kpiNow } from '../lib/product-kpis-emitter';
import {
  OnbBrandHeader,
  OnbProgressTrack,
  OnbPanelHead,
  OnbBtnRow,
  OnbSourceCard,
  OnbTimelineItem,
  type OnbSourceKey,
  type OnbConnectionKind,
} from '../components/OnbStep';

// ------------------------------------------------------------------ tipos

/**
 * Passos visiveis para o usuario (mapeia ao prototipo de 3 passos).
 * Internamente o fluxo F2 de 4 passos foi colapsado em 3 — ver GAP G-ONB-001.
 */
type VisibleStep = 'account' | 'sources' | 'success';

type SegmentOption = keyof typeof ptBR.onboarding.segmentCards;

const SOURCES: readonly OnbSourceKey[] = ['instagram', 'metaAds', 'googleDrive'];

type SourcesState = Record<OnbSourceKey, OnbConnectionKind>;

// Descricao de fonte quando conectada — reutiliza texto do prototipo
const CONNECTED_LABELS: Record<OnbSourceKey, string> = {
  instagram: 'Conectado com sucesso',
  metaAds: 'Conectado — Meta Business Suite',
  googleDrive: 'Conectado — Google Drive',
};

// Shape de respostas de API (inalterado do F2)
interface CreateTenantResponse {
  readonly tenant_id: string;
  readonly name: string;
}

interface ConnectSourceResponse {
  readonly authorization_url: string;
  readonly state: string;
}

function sourceApiType(key: OnbSourceKey): 'instagram' | 'meta_ads' | 'google_drive' {
  switch (key) {
    case 'instagram':
      return 'instagram';
    case 'metaAds':
      return 'meta_ads';
    case 'googleDrive':
      return 'google_drive';
  }
}

function atLeastOneConnected(sources: SourcesState): boolean {
  return SOURCES.some((k) => sources[k] === 'connected' || sources[k] === 'pending');
}

// ------------------------------------------------------------------ componente principal

export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate();
  const { activeTenantId, hydrateMe } = useAuth();

  // Passo visivel atual
  const [step, setStep] = useState<VisibleStep>('account');

  // ---- passo 1 — account
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  // tenantName: no prototipo o nome pessoal e usado como nome do tenant (G-ONB-002).
  // Manter como estado separado para futura expansao (campo "nome do negocio").
  const [tenantName] = useState('');
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);

  // Segmento interno — colapsado do F2, nao visivel como passo separado (G-ONB-001)
  const segmentRef = useRef<SegmentOption>('outro');

  // ---- passo 2 — sources
  const [sources, setSources] = useState<SourcesState>({
    instagram: 'idle',
    metaAds: 'idle',
    googleDrive: 'idle',
  });
  const [sourcesGlobalError, setSourcesGlobalError] = useState<string | null>(null);

  // ---------------------------------------------------------------- step 1 — account

  const handleAccountSubmit = useCallback(
    async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
      ev.preventDefault();
      setAccountError(null);

      // Validacoes de campo
      if (userName.trim().length === 0) {
        setAccountError(t('errors.required'));
        return;
      }
      if (userEmail.trim().length === 0) {
        setAccountError(t('errors.required'));
        return;
      }
      if (userPassword.length < 8) {
        setAccountError(t('errors.passwordTooShort'));
        return;
      }

      setAccountSubmitting(true);
      try {
        // Criacao de tenant real.
        // Mock local enquanto POST /tenants nao existe (D-FE-002 / G-ONB-003).
        let tenantId: string;
        const nameForTenant = tenantName.trim() || userName.trim();
        try {
          const res = await apiClient.post<CreateTenantResponse>('/tenants', {
            name: nameForTenant,
            segment: segmentRef.current,
          });
          tenantId = res.tenant_id;
        } catch {
          tenantId = activeTenantId ?? `mock-tenant-${Date.now()}`;
        }

        setCreatedTenantId(tenantId);

        await emitKpi({
          type: 'TenantCreated',
          tenant_id: tenantId,
          occurred_at: kpiNow(),
          acquisition_source: 'self-signup',
        });

        setStep('sources');
      } finally {
        setAccountSubmitting(false);
      }
    },
    [userName, userEmail, userPassword, activeTenantId],
  );

  // ---------------------------------------------------------------- step 2 — sources

  const handleConnectSource = useCallback(async (key: OnbSourceKey): Promise<void> => {
    setSourcesGlobalError(null);
    setSources((prev) => ({ ...prev, [key]: 'connecting' as OnbConnectionKind }));
    try {
      const res = await apiClient.post<ConnectSourceResponse>(
        `/sources/connect/${sourceApiType(key)}`,
      );
      window.open(res.authorization_url, '_blank', 'noopener,noreferrer');
      setSources((prev) => ({ ...prev, [key]: 'pending' as OnbConnectionKind }));
    } catch {
      setSources((prev) => ({ ...prev, [key]: 'error' as OnbConnectionKind }));
      setSourcesGlobalError(t('onboarding.sourceConnectErrorMsg'));
    }
  }, []);

  const handleSourcesContinue = useCallback(async (): Promise<void> => {
    const tenantId = createdTenantId ?? activeTenantId ?? 'unknown';
    await emitKpi({
      type: 'FeatureUsed',
      tenant_id: tenantId,
      occurred_at: kpiNow(),
      feature: 'dashboard-opened',
      metadata: { from: 'onboarding' },
    });
    setStep('success');
  }, [createdTenantId, activeTenantId]);

  // ---------------------------------------------------------------- step 3 — success

  /**
   * Encerra o onboarding de verdade.
   *
   * BUG CORRIGIDO 2026-08-11: o backend so considera o onboarding concluido
   * quando existe o evento `FirstInsightViewed`, emitido por
   * `POST /onboarding/complete`. NENHUMA tela chamava esse endpoint — entao
   * `GET /me` devolvia `onboarding_completed: false` para sempre e o
   * roteamento inteligente (D-051) mandava o usuario de volta ao formulario a
   * CADA login. O owner reportou exatamente isso no stage.
   *
   * A chamada e best-effort: se falhar, o usuario ainda entra no produto —
   * repetir o formulario e chato, mas travar a porta e pior.
   */
  const handleGoToMirror = useCallback((): void => {
    void (async (): Promise<void> => {
      try {
        await apiClient.post('/onboarding/complete', {});
        // Reidrata o /me para que o roteamento inteligente ja veja concluido.
        await hydrateMe();
      } catch {
        // Silencioso de proposito: nao bloqueia a entrada no produto.
      }
      void navigate('/mirror', { replace: true });
    })();
  }, [navigate, hydrateMe]);

  // ---------------------------------------------------------------- renders

  const stepNum: 1 | 2 | 3 = step === 'account' ? 1 : step === 'sources' ? 2 : 3;

  const renderAccount = (): JSX.Element => (
    <div
      className="onb-panel"
      id="onb-panel-account"
      role="tabpanel"
      aria-labelledby="onb-label-account"
    >
      <OnbPanelHead eyebrow={t('onboarding.stepEyebrow1')}>
        <h1 className="onb-panel-title">{t('onboarding.accountTitle')}</h1>
        <p className="onb-panel-subtitle">{t('onboarding.accountSubtitle')}</p>
      </OnbPanelHead>

      <form
        onSubmit={(ev) => void handleAccountSubmit(ev)}
        noValidate
        aria-labelledby="onb-label-account"
      >
        <div className="onb-panel-body">
          {/* Nome */}
          <div className="onb-field-group">
            <label className="onb-field-label" htmlFor="onb-name">
              {t('onboarding.nameLabel')}
            </label>
            <input
              className="onb-input"
              id="onb-name"
              name="name"
              type="text"
              required
              autoFocus
              autoComplete="name"
              placeholder={t('onboarding.namePlaceholder')}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              aria-describedby={accountError !== null ? 'onb-account-error' : undefined}
            />
          </div>

          {/* E-mail */}
          <div className="onb-field-group">
            <label className="onb-field-label" htmlFor="onb-email">
              {t('onboarding.emailLabel')}
            </label>
            <input
              className="onb-input"
              id="onb-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder={t('onboarding.emailPlaceholder')}
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              aria-describedby={accountError !== null ? 'onb-account-error' : undefined}
            />
          </div>

          {/* Senha */}
          <div className="onb-field-group">
            <label className="onb-field-label" htmlFor="onb-password">
              {t('onboarding.passwordLabel')}
            </label>
            <input
              className="onb-input"
              id="onb-password"
              name="password"
              type="password"
              required
              autoComplete="new-password"
              placeholder={t('onboarding.passwordPlaceholder')}
              value={userPassword}
              onChange={(e) => setUserPassword(e.target.value)}
              aria-describedby={accountError !== null ? 'onb-account-error' : undefined}
            />
          </div>

          {/* Erro de validacao */}
          {accountError !== null ? (
            <p id="onb-account-error" role="alert" className="onb-field-error">
              {accountError}
            </p>
          ) : null}
        </div>

        <OnbBtnRow
          hideBack
          continueLabel={
            accountSubmitting ? t('common.loading') : t('onboarding.continueToSources')
          }
          continueDisabled={accountSubmitting}
          continueIsSubmit
        />
      </form>
    </div>
  );

  const renderSources = (): JSX.Element => (
    <div
      className="onb-panel"
      id="onb-panel-sources"
      role="tabpanel"
      aria-labelledby="onb-label-sources"
    >
      <OnbPanelHead eyebrow={t('onboarding.stepEyebrow2')}>
        <h1 className="onb-panel-title">{t('onboarding.sourcesConnectTitle')}</h1>
        <p className="onb-panel-subtitle">{t('onboarding.sourcesConnectSubtitle')}</p>
      </OnbPanelHead>

      <div className="onb-panel-body">
        {/* Erro global de conexao */}
        {sourcesGlobalError !== null ? (
          <p role="alert" className="onb-field-error">
            {sourcesGlobalError}
          </p>
        ) : null}

        {/* Lista de fontes */}
        <ul className="onb-source-list" role="list" aria-label={t('onboarding.sourcesTitle')}>
          {SOURCES.map((key) => (
            <OnbSourceCard
              key={key}
              sourceKey={key}
              connectionKind={sources[key]}
              connectedLabel={sources[key] === 'connected' ? CONNECTED_LABELS[key] : undefined}
              onConnect={() => void handleConnectSource(key)}
            />
          ))}
        </ul>

        {/* Nota de seguranca — equivalente ao source-note do prototipo */}
        <div className="onb-security-note" role="note">
          <span className="material-symbols-rounded onb-security-note-icon" aria-hidden="true">
            lock
          </span>
          <p>{t('onboarding.sourcesSecurityNote')}</p>
        </div>
      </div>

      <OnbBtnRow
        onBack={() => setStep('account')}
        onContinue={() => void handleSourcesContinue()}
        continueLabel={
          atLeastOneConnected(sources)
            ? t('onboarding.continueToSources')
            : t('onboarding.skipSources')
        }
        continueDisabled={false}
      />
    </div>
  );

  const renderSuccess = (): JSX.Element => (
    <div
      className="onb-panel"
      id="onb-panel-success"
      role="tabpanel"
      aria-labelledby="onb-label-success"
    >
      <OnbPanelHead eyebrow={t('onboarding.stepEyebrow3')} centered>
        {/* Icone de sucesso */}
        <div className="onb-success-icon-wrap" aria-hidden="true">
          <span className="material-symbols-rounded onb-success-icon">check</span>
        </div>
        <h1 className="onb-panel-title" style={{ textAlign: 'center', marginBottom: '8px' }}>
          {t('onboarding.successTitle')}
        </h1>
        <p className="onb-panel-subtitle" style={{ textAlign: 'center' }}>
          {t('onboarding.successSubtitle')}
        </p>
      </OnbPanelHead>

      <div className="onb-panel-body">
        <ul className="onb-timeline-list" role="list" aria-label={t('onboarding.successTitle')}>
          <OnbTimelineItem
            icon="check_circle"
            done
            label={t('onboarding.timelineItem1Label')}
            meta={t('onboarding.timelineItem1Meta')}
          />
          <OnbTimelineItem
            icon="schedule"
            done={false}
            label={t('onboarding.timelineItem2Label')}
            meta={t('onboarding.timelineItem2Meta')}
          />
          <OnbTimelineItem
            icon="insights"
            done={false}
            label={t('onboarding.timelineItem3Label')}
            meta={t('onboarding.timelineItem3Meta')}
          />
        </ul>
      </div>

      {/* CTA final — largura total, sem linha de botao duplo */}
      <div className="onb-btn-cta-wrap">
        <button type="button" className="onb-btn-cta" onClick={handleGoToMirror}>
          <span className="material-symbols-rounded onb-btn-icon" aria-hidden="true">
            dashboard
          </span>
          {t('onboarding.ctaDashboard')}
        </button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------- root render

  return (
    <main className="onboarding-shell" role="main" aria-label={t('onboarding.title')}>
      {/* Logo + nome CROSS */}
      <OnbBrandHeader />

      {/* Barra de progresso com labels — fiel ao prototipo */}
      <OnbProgressTrack currentStep={stepNum} />

      {/* Card principal */}
      <div className="onb-step-card" id="onb-step-card">
        {/* IDs ocultos para aria-labelledby */}
        <span id="onb-label-account" className="sr-only">
          {t('onboarding.stepLabel1')}
        </span>
        <span id="onb-label-sources" className="sr-only">
          {t('onboarding.stepLabel2')}
        </span>
        <span id="onb-label-success" className="sr-only">
          {t('onboarding.stepLabel3')}
        </span>

        {step === 'account' && renderAccount()}
        {step === 'sources' && renderSources()}
        {step === 'success' && renderSuccess()}
      </div>
    </main>
  );
}
